/**
 * HA-575 outcome copy for the operator job actions, following the command-outcome pattern: a brief
 * confirm on success, and on failure a named cause with exactly ONE recovery action.
 *
 * Retryability is never read from a stored flag — the backend derives it from the error code
 * (D-HA-1 D6) and this module names the result.
 */

export type PrintJobRecovery = "retry" | "refresh" | "reprint" | "escalate";

export interface PrintJobFailure {
	title: string;
	description: string;
	recovery: PrintJobRecovery;
}

export const CANCEL_TOO_LATE_TITLE = "Cancel too late — print has started";

export const CANCEL_SUCCESS = "Print cancelled";
export const RETRY_SUCCESS = "Retry sent — watch the printer";
export const REPRINT_SUCCESS =
	"Reprint sent — check the printer for the receipt";

export function describeCancelFailure(
	code: string | undefined,
	fallback?: string,
): PrintJobFailure {
	switch (code) {
		// The job started printing between the operator looking and tapping (AC3).
		case "CONFLICT":
		case "NOT_CANCELLABLE":
			return {
				title: CANCEL_TOO_LATE_TITLE,
				description:
					"The printer had already started this job, so it could not be stopped.",
				recovery: "refresh",
			};

		case "NOT_FOUND":
			return {
				title: "The printer no longer has this job",
				description:
					"It may have finished or expired. Refresh to see where it got to.",
				recovery: "refresh",
			};

		case "TRANSPORT_ERROR":
			return {
				title: "Could not reach the printer",
				description:
					"The device is online but the printer did not answer, so the job was left alone.",
				recovery: "retry",
			};

		case "TIMEOUT":
			return {
				title: "The device did not respond in time",
				description:
					"The cancel may or may not have landed. Refresh to see the job's current state.",
				recovery: "refresh",
			};

		case "PUBLISHER_NOT_READY":
			return {
				title: "Cannot reach devices right now",
				description:
					"The platform's connection to the device network is unavailable.",
				recovery: "retry",
			};

		default:
			return {
				title: "Could not cancel the print",
				description: fallback || "The cancel could not be sent to the printer.",
				recovery: "refresh",
			};
	}
}

export function describeRetryFailure(
	code: string | undefined,
	fallback?: string,
): PrintJobFailure {
	switch (code) {
		case "RETRY_LIMIT_REACHED":
			return {
				title: "No retries left for this job",
				description:
					"This job has been retried the maximum number of times. Someone will need to look at the printer.",
				recovery: "escalate",
			};

		case "NOT_RETRYABLE":
			return {
				title: "This failure cannot be retried",
				description:
					"The printer cannot re-run this job. Reprint it instead to send the original again.",
				recovery: "reprint",
			};

		case "NOT_FOUND":
			return {
				title: "The printer no longer has this job",
				description:
					"It has been dropped from the printer's queue, so there is nothing to re-run. Reprint sends the original again.",
				recovery: "reprint",
			};

		case "TRANSPORT_ERROR":
			return {
				title: "Could not reach the printer",
				description:
					"The device is online but the printer did not answer — check it is powered on and connected.",
				recovery: "retry",
			};

		case "TIMEOUT":
			return {
				title: "The device did not respond in time",
				description: "The retry may still have landed. Refresh to see.",
				recovery: "refresh",
			};

		default:
			return {
				title: "Could not retry the print",
				description: fallback || "The retry could not be sent to the printer.",
				recovery: "refresh",
			};
	}
}

export function describeReprintFailure(
	code: string | undefined,
	fallback?: string,
): PrintJobFailure {
	switch (code) {
		// The 30-day retention window has passed (HA-639).
		case "PAYLOAD_UNAVAILABLE":
			return {
				title: "The original print is no longer stored",
				description:
					"Prints are kept for a limited time, and this one has passed it, so it cannot be reproduced exactly.",
				recovery: "refresh",
			};

		case "PUBLISH_FAILED":
			return {
				title: "Could not send the reprint",
				description:
					"The reprint could not be handed to the device network. Try again shortly.",
				recovery: "retry",
			};

		default:
			return {
				title: "Could not reprint",
				description: fallback || "The reprint could not be sent.",
				recovery: "refresh",
			};
	}
}

/** Human label for an OutboundJob status, which arrives PascalCased from the backend enum. */
export function printJobStatusLabel(status: string): string {
	switch (status) {
		case "PendingPublish":
			return "Sending";
		case "Queued":
			return "Queued";
		case "Printing":
			return "Printing";
		case "Completed":
			return "Printed";
		case "Failed":
			return "Failed";
		case "Cancelled":
			return "Cancelled";
		case "Interrupted":
			return "Interrupted";
		case "Expired":
			return "Expired";
		default:
			return status;
	}
}

/**
 * Escalation hands over the two things whoever picks it up actually needs: which device, and what
 * it last said. Pre-filled per AC11 rather than asking the operator to retype it.
 */
export function buildEscalationSummary(job: {
	hostname: string;
	printerId: string;
	outboundJobId: string;
	errorCode: string | null;
	errorMessage: string | null;
}): string {
	return [
		`Device: ${job.hostname}`,
		`Printer: ${job.printerId}`,
		`Job: ${job.outboundJobId}`,
		`Last error: ${job.errorCode ?? "unknown"}${job.errorMessage ? ` — ${job.errorMessage}` : ""}`,
	].join("\n");
}
